import React, { useMemo, memo } from 'react';
import { Crown, ArrowRight } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { SectionHeader } from '../../ui/SectionHeader';
import { AppState } from '../../../app/types';
import { ACHIEVEMENTS } from '../../../mascot/achievements/achievementData';
import type { AchievementCheckData } from '../../../mascot/achievements/achievementData';
import { AchievementProgressBar } from './AchievementProgressBar';
interface AchievementsSectionProps {
  state: AppState;
  totalQuestionsAnswered: number;
  masteredCount: number;
  currentStreak: number;
  onNavigate: (view: string) => void;
}
const RARITY_WEIGHT: Record<string, number> = {
  legendary: 4,
  rare: 3,
  uncommon: 2,
  common: 1
};
export const AchievementsSection = memo(function AchievementsSection({
  state,
  totalQuestionsAnswered,
  masteredCount,
  currentStreak,
  onNavigate
}: AchievementsSectionProps) {
  // Streak data from state
  const currentWeekDays = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  const longestTotalStreak = state.streak?.longestTotalStreak || 0;
  // Lifetime counters (HC-7/HC-8 fix). Fallback: derive total daily goals from
  // weeklyHistory for pre-migration data.
  const totalDailyGoalsReached = state.dailyStreak?.totalDailyGoalsReached ?? (state.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state.dailyStreak?.totalPerfectSessions ?? 0;
  const checkData: AchievementCheckData = useMemo(() => ({
    totalQuestionsAnswered,
    masteredCount,
    level: state.stats.level,
    xp: state.stats.xp,
    currentWeekDays,
    consecutiveFullWeeks,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions
  }), [totalQuestionsAnswered, masteredCount, state.stats.level, state.stats.xp, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions]);
  const evaluated = useMemo(() => ACHIEVEMENTS.map(achievement => ({
    ...achievement,
    isUnlocked: achievement.check(checkData),
    progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
  })), [checkData]);
  const unlockedCount = evaluated.filter(a => a.isUnlocked).length;
  const totalCount = evaluated.length;
  // Highlight: a few unlocked achievements, prioritising the rarest.
  const recentlyUnlocked = useMemo(() => {
    return evaluated.filter(a => a.isUnlocked).sort((a, b) => RARITY_WEIGHT[b.rarity] - RARITY_WEIGHT[a.rarity]).slice(0, 3);
  }, [evaluated]);
  return <Card className="p-5 sm:p-6 bg-white border-2 border-slate-100 shadow-sm" data-id="element-1760" data-mp="1w0ht0rotikqw">
      <SectionHeader title="Achievements" icon={Crown} accentColor="bg-amber-100 text-amber-600" description={`${unlockedCount} van de ${totalCount} behaald`} action={<Button onClick={() => onNavigate('collection')} className="w-full sm:w-auto bg-amber-100 text-amber-700 hover:bg-amber-200 border-none" data-id="element-1762" data-mp="37i3vfhtjbwc0">
            Bekijk alle <ArrowRight className="w-4 h-4 ml-2" data-id="element-1763" data-mp="33w0dcpt07gr" />
          </Button>} className="mb-4" data-id="element-1761" data-mp="1vyet9fxv5d8z" />
      {/* Overall completion — turns "how close am I to 100%?" into a glanceable bar */}
      <div className="mb-5" data-id="element-1764" data-mp="3qgzqevd7weqw">
        <AchievementProgressBar current={unlockedCount} max={totalCount} layout="labelled" tone="amber" ariaLabel={`${unlockedCount} van de ${totalCount} achievements behaald`} data-id="element-1765" data-mp="3qg6gxmmvjb52" />
      </div>
      <div className="flex-1 w-full" data-id="element-1766" data-mp="mtzhdc5qoppm">
        {recentlyUnlocked.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-1767" data-mp="hzldeld0ha9c">
            {recentlyUnlocked.map(achievement => <div key={achievement.id} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5" data-id="element-1768" data-mp="1kcbjatzxe05">
                <span className="text-sm" aria-hidden="true" data-id="element-1769" data-mp="3orxu4zz4270z">
                  {achievement.emoji}
                </span>
                <span className="text-xs font-bold text-slate-700 truncate max-w-[100px]" data-id="element-1770" data-mp="3trk9pg2evzcy">
                  {achievement.name}
                </span>
              </div>)}
          </div> : <p className="text-sm text-slate-400 italic" data-id="element-1771" data-mp="bjusdiem0jnz">
            Nog geen achievements behaald — begin met oefenen om je eerste te
            verdienen!
          </p>}
      </div>
    </Card>;
});